import React, { useEffect, useState } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useToast, Toast } from 'contexts/ToastContext'
import { cn } from 'lib/utils'
import { useTranslation } from 'react-i18next'

export function ToastContainer() {
  const { toasts, removeToast } = useToast()
  const { t } = useTranslation()

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  const getToastStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200'
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
    }
  }

  if (toasts.length === 0) return null

  return (
    <div
      className="
        fixed bottom-4 left-4 right-4 sm:bottom-6 sm:right-6 sm:left-auto
        z-[200] space-y-3 pointer-events-none flex flex-col items-stretch sm:items-end
      "
      style={{ maxWidth: 'min(420px, calc(100vw - 2rem))' }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'relative p-4 rounded-xl border shadow-lg transform transition-all duration-300 ease-in-out bg-white/95 backdrop-blur pointer-events-auto',
            'text-sm sm:text-base w-full',
            'animate-in slide-in-from-right-full',
            getToastStyles(toast.type)
          )}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              {getIcon(toast.type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold">
                    {toast.title || t('common.info')}
                  </h4>
                  <p className="text-sm mt-1 opacity-90">
                    {toast.message || ''}
                  </p>
                  {toast.action && (
                    <button
                      onClick={toast.action.onClick}
                      className="text-sm font-medium underline mt-2 hover:no-underline"
                    >
                      {toast.action.label}
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => removeToast(toast.id)}
                  className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Progress bar for timed toasts */}
          {toast.duration !== 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-lg overflow-hidden">
              <div 
                className="h-full bg-current opacity-30 animate-pulse"
                style={{
                  animation: `toast-progress ${toast.duration || 5000}ms linear forwards`
                }}
              />
            </div>
          )}
        </div>
      ))}
      
      <style>{`
        @keyframes toast-progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  )
}
